import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowRight } from 'lucide-react';
import { useHostState } from '@/hooks/useHostState';
import { useMyProducers } from '@/hooks/useMyProducers';

export function HostCard() {
  const { hostState, isLoading: entryLoading } = useHostState();
  const { producers, isLoading: producersLoading } = useMyProducers();
  const firstProducer = producers?.[0];
  const hostCardState = hostState.kind;
  const isDraft = firstProducer && firstProducer.status !== 'public';

  if (entryLoading) {
    return (
      <div>
        <Skeleton className="h-4 w-32 mb-3" />
        <Card className="border-border/60 shadow-none">
          <CardContent className="p-5 sm:p-6">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-56 mt-2" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (hostCardState === 'active_host') {
    return (
      <div>
        <h2 className="text-sm font-medium text-muted-foreground/70 mb-3">Din plats</h2>
        <Link
          to={firstProducer?.status === 'public'
            ? `/app/producer/farm/${firstProducer?.id}`
            : `/app/producer/farm/${firstProducer?.id}/setup`}
          className="block group"
        >
          <Card className={`shadow-none transition-colors ${isDraft ? 'border-primary/30 border-l-[3px] border-l-primary/50 bg-primary/[0.03]' : 'border-primary/20 hover:border-primary/40'}`}>
            <CardContent className="p-5 sm:p-6">
              {producersLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-4 w-56" />
                </div>
              ) : firstProducer ? (
                <div className="min-w-0">
                  <p className="font-medium text-foreground truncate">{firstProducer.name}</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    {isDraft ? (
                      <span className="text-[11px] text-muted-foreground/50 tracking-wide uppercase">
                        Utkast
                      </span>
                    ) : (
                      <p className="text-sm text-muted-foreground">Publicerad</p>
                    )}
                  </div>
                  {isDraft && (
                    <p className="text-xs text-muted-foreground/60 mt-1.5">
                      Din plats är nästan klar. Fyll i resten för att kunna publicera.
                    </p>
                  )}
                </div>
              ) : null}
              <div className="flex items-center justify-between mt-4">
                {isDraft ? (
                  <Button size="sm" className="text-sm">
                    Färdigställ
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" className="text-sm">
                    Öppna
                  </Button>
                )}
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              {firstProducer && (
                <Link
                  to={`/app/producer/farm/${firstProducer.id}`}
                  className="block text-xs text-muted-foreground/60 hover:text-muted-foreground mt-2 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  Hantera i värdportalen →
                </Link>
              )}
            </CardContent>
          </Card>
        </Link>
      </div>
    );
  }

  if (hostCardState === 'interest_sent') {
    return (
      <div>
        <h2 className="text-sm font-medium text-muted-foreground/70 mb-3">Bli värd i Farmly</h2>
        <Card className="border-border/40 shadow-none bg-muted/30">
          <CardContent className="p-5 sm:p-6">
            <p className="font-medium text-foreground">Intresse skickat</p>
            <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
              Vi återkommer via e-post när vi har läst igenom er ansökan.
            </p>
            <p className="text-xs text-muted-foreground/60 mt-2">
              Du behöver inte göra något mer just nu.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // not_host
  return (
    <div>
      <h2 className="text-sm font-medium text-muted-foreground/70 mb-3">Bli värd i Farmly</h2>
      <Link to="/app/producer" className="block group">
        <Card className="border-border/60 shadow-none transition-colors hover:border-border">
          <CardContent className="p-5 sm:p-6 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="font-medium text-foreground">Bli värd i Farmly</p>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                Erbjud din plats eller dina aktiviteter via Farmly.
              </p>
            </div>
            <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground/40 opacity-0 group-hover:opacity-100 transition-opacity" />
          </CardContent>
        </Card>
      </Link>
    </div>
  );
}
